/**
 * MailboxPolicySection — Iter Feb 2026 R47 · Phase 3.
 * Workspace-level policy for mailboxes:
 *   - Allow personal mailboxes (per-user IMAP inboxes) yes/no
 *   - Cap number of personal mailboxes per user (default 3)
 *   - Allow workspace-shared mailboxes yes/no
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Users } from "lucide-react";

export default function MailboxPolicySection() {
  const { activeWorkspaceId } = useAuth();
  const [form, setForm] = useState({
    allow_personal_mailboxes: true,
    personal_cap: 3,
    allow_workspace_shared: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/settings/mailbox-policy`)
      .then(r => setForm(f => ({ ...f, ...r.data })))
      .catch(() => {});
  }, [activeWorkspaceId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/settings/mailbox-policy`, {
        allow_personal_mailboxes: !!form.allow_personal_mailboxes,
        personal_cap: parseInt(form.personal_cap, 10) || 0,
        allow_workspace_shared: !!form.allow_workspace_shared,
      });
      toast.success("Mailbox policy saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border p-6 space-y-4" data-testid="mailbox-policy-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 border border-border flex items-center justify-center bg-purple-50">
          <Users className="w-5 h-5 text-purple-600" />
        </div>
        <div className="flex-1">
          <div className="font-bold text-sm">Mailbox policy</div>
          <p className="text-xs text-muted-foreground">
            Control which mailboxes members can connect from the Webmail page.
          </p>
        </div>
      </div>

      <label className="flex items-center justify-between border-l-2 border-purple-500/60 pl-3 py-1 cursor-pointer">
        <div>
          <div className="text-sm font-semibold">Allow personal mailboxes</div>
          <div className="text-[11px] text-muted-foreground">Members can add their own IMAP inbox (only visible to them).</div>
        </div>
        <input type="checkbox" checked={!!form.allow_personal_mailboxes}
               onChange={e => setForm({ ...form, allow_personal_mailboxes: e.target.checked })}
               data-testid="policy-allow-personal" />
      </label>

      <div className="border-l-2 border-purple-500/60 pl-3 py-1 flex items-center gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold">Personal mailbox cap (per user)</div>
          <div className="text-[11px] text-muted-foreground">0 = unlimited. Recommended: 3.</div>
        </div>
        <Input type="number" min={0} max={25}
               value={form.personal_cap}
               onChange={e => setForm({ ...form, personal_cap: e.target.value })}
               className="w-20 h-9 rounded-none text-right"
               disabled={!form.allow_personal_mailboxes}
               data-testid="policy-personal-cap" />
      </div>

      <label className="flex items-center justify-between border-l-2 border-purple-500/60 pl-3 py-1 cursor-pointer">
        <div>
          <div className="text-sm font-semibold">Allow workspace-shared mailboxes</div>
          <div className="text-[11px] text-muted-foreground">Admins can add mailboxes visible to all members (e.g. <code>support@yourdomain.com</code>).</div>
        </div>
        <input type="checkbox" checked={!!form.allow_workspace_shared}
               onChange={e => setForm({ ...form, allow_workspace_shared: e.target.checked })}
               data-testid="policy-allow-shared" />
      </label>

      <div className="flex justify-end pt-2 border-t border-border">
        <Button onClick={save} disabled={busy} className="rounded-none" data-testid="policy-save">
          {busy ? "Saving…" : <><Save className="w-4 h-4 mr-1.5" /> Save policy</>}
        </Button>
      </div>
    </div>
  );
}
